/**
 * Touch detection and the fake pointer lock. Importing this module is enough:
 * on a touch device the Pointer Lock API is replaced by a flag, so the game's
 * "mouse captured" paths (look, mine, place, hotbar) run unchanged without a
 * real lock (mobile browsers have none). Desktop keeps the real API untouched
 * until a first real touchstart, so a hybrid laptop still plays with the mouse.
 */

let touchActive = false;
let fakeLocked = false;
const listeners: Array<() => void> = [];

export function touchEnabled(): boolean { return touchActive; }
export function onTouchEnabled(fn: () => void): void { if (touchActive) fn(); else listeners.push(fn); }

function detect(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  try {
    if (new URLSearchParams(location.search).get('touch') === '1') return true;
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
  } catch { /* no matchMedia */ }
  return false;
}

function installFakeLock(): void {
  const lockEl = (el: Element): void => {
    if (fakeLocked) return;
    fakeLocked = true;
    lockTarget = el;
    document.dispatchEvent(new Event('pointerlockchange'));
  };
  let lockTarget: Element | null = null;
  Object.defineProperty(document, 'pointerLockElement', { configurable: true, get: () => (fakeLocked ? lockTarget : null) });
  Element.prototype.requestPointerLock = function (this: Element) { lockEl(this); return Promise.resolve() as never; };
  document.exitPointerLock = (): void => {
    if (!fakeLocked) return;
    fakeLocked = false;
    document.dispatchEvent(new Event('pointerlockchange'));
  };
}

export function enableTouch(): void {
  if (touchActive) return;
  touchActive = true;
  // a real lock held by the mouse is dropped first so the flag takes over cleanly
  try { document.exitPointerLock?.(); } catch { /* not locked */ }
  installFakeLock();
  for (const fn of listeners.splice(0)) fn();
}

if (detect()) enableTouch();
else if (typeof window !== 'undefined') {
  window.addEventListener('touchstart', (e) => { if (e.isTrusted) enableTouch(); }, { once: true, passive: true });
}
